/**
 * Title resolution for custom (Storyblok-configured) line items.
 *
 * A custom line item carries its own titles in the transaction's metadata
 * (`customLineItem.title[locale]`). On top of that, a listing or transaction may
 * have a Storyblok translation bundle that overrides those titles. The bundle
 * wins where it has an entry for the line item code, the metadata title is the
 * fallback, English is the last resort.
 *
 * The bundle is frequently absent, and that is a legitimate state rather than an
 * error: `CheckoutPage` reads it as `translations?.customLineItems` and
 * `ListingPage` as `translations.customLineItems`, both `undefined` for every
 * listing that has no custom line item translations configured. `TransactionPanel`
 * already resolves it to `{}` explicitly, and `BookingPanel`, `SectionHeading` and
 * `EstimatedBreakdownMaybe` all default the prop to `{}` — the fallback chain
 * below exists precisely so the metadata title is rendered instead.
 *
 * The two breakdown components that read the bundle did not default it, so on a
 * checkout for a listing without translations the first custom line item threw
 * `undefined is not an object` and the error boundary replaced the whole booking
 * breakdown (VANSITE-FRONTEND-4RQ). The chain lives here, in one place, because
 * the two copies it replaces were character-for-character identical and would
 * have drifted.
 */

/**
 * Shared empty bundle.
 *
 * Stable identity on purpose: a `= {}` default parameter allocates a fresh object
 * on every render, and a fresh object prop defeats `memo` on every component that
 * receives it.
 *
 * @type {Readonly<Object<string, {title?: Object<string, string>}>>}
 */
export const EMPTY_CUSTOM_LINE_ITEM_TRANSLATIONS = Object.freeze({});

/**
 * Resolves the display title of a custom line item.
 *
 * @param {string} code - line item code, e.g. `line-item/extra-storyblok-item`
 * @param {{title: Object<string, string>}} customLineItem - entry from the
 *   transaction metadata's `customLineItems`
 * @param {Object<string, {title?: Object<string, string>}>} [customLineItemTranslations] -
 *   Storyblok translation bundle; absent when the listing has none
 * @param {string} locale - active `intl.locale`
 * @returns {string} the resolved title, or `''` when nothing resolves
 */
export const resolveCustomLineItemTitle = (
  code,
  customLineItem,
  customLineItemTranslations,
  locale,
) => {
  const translations = customLineItemTranslations || EMPTY_CUSTOM_LINE_ITEM_TRANSLATIONS;

  return (
    translations[code]?.title || customLineItem.title[locale] || customLineItem.title['en'] || ''
  );
};
